import { useStore } from 'vuex';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import router from '@/routes/routes';
import 'sweetalert2/src/sweetalert2.scss';

/**
 * Composable para gestionar la API de usuario (postulaciones y reportes de pago)
 * @returns {Object} - Métodos para gestionar postulaciones y reportes de pago
 */
export function useUserAPI() {
  const store = useStore();

  /**
   * Obtiene la lista de postulaciones del usuario con paginación
   * @param {Object} params - Parámetros de búsqueda y paginación
   * @returns {Promise<Object>} - Objeto con { data, meta }
   */
  const getUserPlatforms = async (params = {}) => {
    const requestParams = {
      url: "user/user_platform/all",
      params: {
        active: 1,
        page: 1,
        limit: 10,
        order_value: "updated_at",
        order_value: "desc",
        ...params,
      },
    };

    try {
      const response = await store.dispatch('getData', requestParams);
      if (response.code === 200) {
        return {
          data: response.data.data,
          meta: response.data.meta,
        };
      }
      return { data: [], meta: {} };
    } catch (error) {
      console.error('Error loading user platforms:', error);
      Swal.fire({
        title: "Error",
        text: "Ocurrió un error al cargar las postulaciones",
        icon: "error",
        showConfirmButton: true,
      });
      return { data: [], meta: {} };
    }
  };

  /**
   * Elimina una postulación del usuario
   * @param {Object} platformData - Datos de la postulación a eliminar
   * @returns {Promise<boolean>} - True si se eliminó correctamente
   */
  const deleteUserPlatform = async (platformData) => {
    const result = await Swal.fire({
      title: "¿Estás seguro de eliminar esta postulación?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      reverseButtons: true,
      showCancelButton: true,
      confirmButtonColor: "#CE0D0D",
      cancelButtonColor: "#9F9F9F",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sí, eliminar",
    });

    if (!result.value) {
      return false;
    }

    const params = {
      url: "user/user_platform/delete",
      data: { id: platformData.id },
    };

    try {
      const response = await store.dispatch('deleteData', params);
      if (response.code === 200) {
        Swal.fire({
          title: "Postulación eliminada correctamente",
          icon: "success",
          showConfirmButton: true,
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting user platform:', error);
      Swal.fire({
        title: "Error",
        text: "Ocurrió un error al eliminar la postulación",
        icon: "error",
        showConfirmButton: true,
      });
      return false;
    }
  };

  /**
   * Crea un reporte de pago
   * @param {Object} paymentData - Datos del pago
   * @param {Object} paymentData.payment_report_id - ID del reporte existente
   * @param {number} paymentData.id_company_bank - ID del banco
   * @param {number} paymentData.id_bcv_rate - ID de la tasa BCV
   * @param {string} paymentData.reference - Referencia del pago
   * @param {string} paymentData.payment_method - Método de pago (pago_movil|transferencia)
   * @param {string} paymentData.origin_bank - Código del banco emisor (4 chars)
   * @param {number} paymentData.amount - Monto del pago
   * @param {string} paymentData.date - Fecha del pago (YYYY-MM-DD)
   * @param {string} paymentData.phone - Teléfono (opcional, requerido para pago_movil)
   * @param {string} paymentData.url_image - URL de la imagen del comprobante
   * @returns {Promise<boolean>} - True si se guardó correctamente
   */
  const createPaymentReport = async (paymentData) => {
    const {
      payment_report_id,
      id_company_bank,
      id_bcv_rate,
      reference,
      payment_method,
      origin_bank,
      origin_bank_code,
      amount,
      date,
      phone,
      url_image,
      currency,
    } = paymentData;

    // Validaciones
    if (!id_company_bank) {
      Swal.fire({
        title: "Seleccione un banco",
        icon: "warning",
        showConfirmButton: true,
      });
      return false;
    }

    if (!origin_bank) {
      Swal.fire({
        title: "Ingrese el banco emisor",
        icon: "warning",
        showConfirmButton: true,
      });
      return false;
    }

    if (!date) {
      Swal.fire({
        title: "Seleccione la fecha del pago",
        icon: "warning",
        showConfirmButton: true,
      });
      return false;
    }

    if (!amount || amount === 0) {
      Swal.fire({
        title: "Ingrese el monto pagado",
        icon: "warning",
        showConfirmButton: true,
      });
      return false;
    }

    if (!reference) {
      Swal.fire({
        title: "Ingrese la referencia del pago",
        icon: "warning",
        showConfirmButton: true,
      });
      return false;
    }

    if (payment_method === 'pago_movil' && !phone) {
      Swal.fire({
        title: "Ingrese el teléfono para Pago Móvil",
        icon: "warning",
        showConfirmButton: true,
      });
      return false;
    }

    // Confirmación
    const result = await Swal.fire({
      title: "¿Estás seguro de reportar este pago?",
      icon: "question",
      reverseButtons: true,
      showCancelButton: true,
      confirmButtonColor: "#B49B57",
      cancelButtonColor: "#9F9F9F",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sí, reportar",
    });

    if (!result.value) {
      return false;
    }

    // Enviar datos
    const formData = new FormData();
    formData.append("payment_report_id", payment_report_id);
    formData.append("id_user_platform", payment_report_id);
    formData.append("id_company_bank", id_company_bank);
    formData.append("id_bcv_rate", id_bcv_rate);
    formData.append("reference", reference);
    formData.append("payment_method", payment_method);
    formData.append("origin_bank", origin_bank);
    formData.append("origin_bank_code", origin_bank_code || "");
    formData.append("amount", amount);
    formData.append("date", new Date(date).toISOString().slice(0, 10));
    formData.append("phone", phone || "");
    formData.append("currency", currency || "VES");
    formData.append("id_user", Cookies.get("id") || "");
    
    if (url_image) {
      formData.append("url_image", url_image);
    }

    try {
      const response = await store.dispatch('createData', {
        url: "user/payment_report/save",
        data: formData,
      });

      if (response.code === 200) {
        const result = await Swal.fire({
          title: 'Pago registrado correctamente',
          icon: "success",
          showCancelButton: true,
          confirmButtonColor: "#B49B57",
          cancelButtonColor: "#9F9F9F",
          confirmButtonText: `Ir a "Mis pagos"`,
          cancelButtonText: "Permanecer en la vista",
          reverseButtons: true,
        });

        if (result.isConfirmed) {
          setTimeout(() => {
            router.push({ name: 'MyPayments' });
          }, 400);
        }
        return true;
      } else {
        Swal.fire({
          title: "Error",
          text: response?.alert || response?.message || "Ocurrió un error al registrar el pago",
          icon: "error",
          showConfirmButton: true,
        });
        return false;
      }
    } catch (error) {
      console.error('Error saving payment report:', error);
      Swal.fire({
        title: "Error",
        text: error?.alert || error?.message || "Ocurrió un error al registrar el pago",
        icon: "error",
        showConfirmButton: true,
      });
      return false;
    }
  };

  return {
    getUserPlatforms,
    deleteUserPlatform,
    createPaymentReport,
  };
}
